import React, { useState, useEffect, useCallback } from "react";
import {
  FaSwimmingPool,
  FaPlus,
  FaTrash,
  FaPen,
  FaTimes,
  FaCheck,
  FaClock,
  FaDollarSign,
  FaCalendarDay,
  FaBroom,
} from "react-icons/fa";
import { db } from "../../firebase";
import { tolerantId } from "../../utils/firestoreHelpers";
import { cargarPersonalEdificio } from "../../utils/personalEdificio";
import { getAllActiveFunds } from "../../utils/fundHelpers";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { useToast } from "../../components/Toast";
import { useLatestRate } from "../../utils/exchangeRates";
import { useAppDialog } from "../../components/AppDialog";
import LoadingState from "../../components/LoadingState";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnGhost,
  inputBase,
  modalOverlay,
} from "../../utils/designTokens";

// ============================================================
// Normaliza documentos legacy de amenities al esquema nuevo
// ============================================================
export const normalizeAmenity = (raw) => {
  const data = { ...raw };
  // Construye array de turnos desde costDay/costNight si falta
  if (!data.turns || !Array.isArray(data.turns) || data.turns.length === 0) {
    const turns = [];
    turns.push({
      name: "Mediodía",
      startTime: "12:00",
      endTime: "18:00",
      cost: Number(data.costDay) || 0,
    });
    turns.push({
      name: "Noche",
      startTime: "18:00",
      endTime: "00:00",
      cost: Number(data.costNight) || 0,
    });
    data.turns = turns;
  }
  // Asegura valores por defecto para campos nuevos
  if (!data.category) {
    const hasCost = (data.turns || []).some((t) => (Number(t.cost) || 0) > 0) ||
      (Number(data.costDay) || 0) > 0 || (Number(data.costNight) || 0) > 0;
    data.category = hasCost ? "con_costo" : "sin_costo";
  }
  if (!data.name) data.name = data.amenity || "";
  if (data.currency === "peso") data.currency = "UYU";
  if (data.currency === "dolar") data.currency = "USD";
  if (!data.currency) data.currency = "UYU";
  if (data.exclusive === undefined) data.exclusive = true;
  if (!data.maxCapacity) data.maxCapacity = 1;
  if (!data.status) data.status = "active";
  if (!data.availableDays) data.availableDays = [0, 1, 2, 3, 4, 5, 6];
  // R6: fechas bloqueadas puntuales (ISO "YYYY-MM-DD") y recurrentes ("MM-DD", todos los años)
  if (!Array.isArray(data.blockedDates)) data.blockedDates = [];
  if (!Array.isArray(data.recurringBlockedDates)) data.recurringBlockedDates = [];
  // R6+: fechas de sorteo (se anota, no reserva) — separadas de las bloqueadas
  if (!Array.isArray(data.raffleDates)) data.raffleDates = [];
  if (!Array.isArray(data.recurringRaffleDates)) data.recurringRaffleDates = [];
  if (data.requiresReservation === undefined) data.requiresReservation = true;
  if (!data.description) data.description = "";
  if (!data.imageUrl) data.imageUrl = "";
  // Reglas
  if (!data.rules) {
    data.rules = {
      minAdvanceDays: data.minAdvanceDays || 0,
      maxActiveReservationsPerUnit: data.maxActiveReservationsPerUnit || 0,
      freeCancellationDays: data.freeCancellationDays || 0,
    };
  }
  if (data.requiresDeposit === undefined) data.requiresDeposit = false;
  if (!data.depositAmount) data.depositAmount = 0;
  // Campos de limpieza post-uso
  if (data.requiresCleaning === undefined) data.requiresCleaning = false;
  if (!data.cleaningEmployeeId) data.cleaningEmployeeId = "";
  // Fondo destino de ingresos
  if (!data.targetFundId) data.targetFundId = "reserva";
  return data;
};

// ============================================================
// Amenity vacío por defecto
// ============================================================
const getEmptyAmenity = () => ({
  name: "",
  description: "",
  imageUrl: "",
  category: "con_costo",
  currency: "UYU",
  requiresReservation: true,
  turns: [
    { name: "Mediodía", startTime: "12:00", endTime: "18:00", cost: 0 },
    { name: "Noche", startTime: "18:00", endTime: "00:00", cost: 0 },
  ],
  availableDays: [1, 2, 3, 4, 5, 6, 0], // Lun-Dom
  blockedDates: [], // R6: fechas puntuales bloqueadas (ISO "YYYY-MM-DD")
  recurringBlockedDates: [], // R6: fechas recurrentes bloqueadas ("MM-DD", todos los años)
  raffleDates: [], // R6+: fechas de sorteo puntuales
  recurringRaffleDates: [], // R6+: fechas de sorteo recurrentes ("MM-DD")
  exclusive: true,
  maxCapacity: 1,
  status: "active",
  rules: { minAdvanceDays: 0, maxActiveReservationsPerUnit: 0, freeCancellationDays: 0 },
  requiresDeposit: false,
  depositAmount: 0,
  requiresCleaning: false,
  cleaningEmployeeId: "",
  targetFundId: "reserva",
});

const DAY_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const STATUS_LABELS = { active: "Activo", inactive: "Inactivo", maintenance: "En mantenimiento" };
const STATUS_COLORS = { active: colors.success, inactive: colors.gray600, maintenance: colors.warning };
const CATEGORY_LABELS = { con_costo: "Con costo", sin_costo: "Sin costo" };
const CATEGORY_COLORS = { con_costo: colors.primary, sin_costo: colors.success };

// ============================================================
// Componente
// ============================================================
const AmenityConfigTab = ({ buildingData }) => {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const { rate: bcuRate, loading: bcuLoading } = useLatestRate();
  const buildingId = buildingData?.buildingid;

  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Fondos disponibles para destino de ingresos
  const activeFunds = getAllActiveFunds(buildingData);

  // Estado del formulario
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creando nuevo
  const [form, setForm] = useState(getEmptyAmenity());
  const [saving, setSaving] = useState(false);
  const [blockDateInput, setBlockDateInput] = useState(""); // R6: input de fecha a bloquear
  const [raffleDateInput, setRaffleDateInput] = useState(""); // R6+: input de fecha de sorteo

  // ============================================================
  // Carga de amenities
  // ============================================================
  const fetchAmenities = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "amenitiesbuilding"),
        where("buildingid", "in", tolerantId(buildingId))
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => normalizeAmenity({ id: d.id, ...d.data() }));
      setAmenities(list);
    } catch (err) {
      console.error("Error cargando amenities:", err);
      showToast("Error al cargar amenities", "error");
    } finally {
      setLoading(false);
    }
  }, [buildingId, showToast]);

  // Personal del edificio (responsable de limpieza): employeebuilding por
  // `building` + nombre de la ficha users. Sin escanear la colección employee.
  const fetchEmployees = useCallback(async () => {
    if (!buildingId) return;
    try {
      setEmployees(await cargarPersonalEdificio(buildingId));
    } catch (err) {
      console.error("Error cargando empleados:", err);
      setEmployees([]);
    }
  }, [buildingId]);

  useEffect(() => {
    fetchAmenities();
    fetchEmployees();
  }, [fetchAmenities, fetchEmployees]);

  // ============================================================
  // Helpers del formulario
  // ============================================================
  const openCreate = () => {
    setEditingId(null);
    setForm(getEmptyAmenity());
    setShowForm(true);
  };

  const openEdit = (amenity) => {
    setEditingId(amenity.id);
    setForm({
      name: amenity.name || amenity.amenity || "",
      description: amenity.description || "",
      imageUrl: amenity.imageUrl || "",
      category: amenity.category || "con_costo",
      currency: amenity.currency || "UYU",
      requiresReservation: amenity.requiresReservation !== false,
      turns: amenity.turns ? amenity.turns.map((t) => ({ ...t })) : getEmptyAmenity().turns,
      availableDays: amenity.availableDays ? [...amenity.availableDays] : [0, 1, 2, 3, 4, 5, 6],
      blockedDates: Array.isArray(amenity.blockedDates) ? [...amenity.blockedDates] : [],
      recurringBlockedDates: Array.isArray(amenity.recurringBlockedDates) ? [...amenity.recurringBlockedDates] : [],
      raffleDates: Array.isArray(amenity.raffleDates) ? [...amenity.raffleDates] : [],
      recurringRaffleDates: Array.isArray(amenity.recurringRaffleDates) ? [...amenity.recurringRaffleDates] : [],
      exclusive: amenity.exclusive !== false,
      maxCapacity: amenity.maxCapacity || 1,
      status: amenity.status || "active",
      rules: amenity.rules ? { ...amenity.rules } : getEmptyAmenity().rules,
      requiresDeposit: amenity.requiresDeposit || false,
      depositAmount: amenity.depositAmount || 0,
      requiresCleaning: amenity.requiresCleaning || false,
      cleaningEmployeeId: amenity.cleaningEmployeeId || "",
      targetFundId: amenity.targetFundId || "reserva",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(getEmptyAmenity());
  };

  const updateForm = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const updateTurn = (idx, field, value) => {
    setForm((f) => {
      const turns = [...f.turns];
      turns[idx] = { ...turns[idx], [field]: value };
      return { ...f, turns };
    });
  };

  const addTurn = () => {
    setForm((f) => ({
      ...f,
      turns: [...f.turns, { name: "", startTime: "08:00", endTime: "12:00", cost: 0 }],
    }));
  };

  const removeTurn = (idx) => {
    setForm((f) => ({ ...f, turns: f.turns.filter((_, i) => i !== idx) }));
  };

  const toggleDay = (dayNum) => {
    setForm((f) => {
      const days = f.availableDays.includes(dayNum)
        ? f.availableDays.filter((d) => d !== dayNum)
        : [...f.availableDays, dayNum];
      return { ...f, availableDays: days };
    });
  };

  // R6: fechas bloqueadas
  const addBlockedDate = (iso) => {
    if (!iso) return;
    setForm((f) => ({
      ...f,
      blockedDates: f.blockedDates.includes(iso) ? f.blockedDates : [...f.blockedDates, iso].sort(),
      raffleDates: f.raffleDates.filter((d) => d !== iso),
    }));
  };
  const removeBlockedDate = (iso) => {
    setForm((f) => ({ ...f, blockedDates: f.blockedDates.filter((d) => d !== iso) }));
  };
  const addRecurringBlocked = (mmdd) => {
    if (!mmdd) return;
    setForm((f) => ({
      ...f,
      recurringBlockedDates: f.recurringBlockedDates.includes(mmdd) ? f.recurringBlockedDates : [...f.recurringBlockedDates, mmdd].sort(),
      recurringRaffleDates: f.recurringRaffleDates.filter((d) => d !== mmdd),
    }));
  };
  const removeRecurringBlocked = (mmdd) => {
    setForm((f) => ({ ...f, recurringBlockedDates: f.recurringBlockedDates.filter((d) => d !== mmdd) }));
  };
  const addYearEndHolidays = () => {
    setForm((f) => {
      const preset = ["12-24", "12-25", "12-31", "01-01"];
      const merged = Array.from(new Set([...(f.recurringBlockedDates || []), ...preset])).sort();
      return { ...f, recurringBlockedDates: merged, recurringRaffleDates: (f.recurringRaffleDates || []).filter((d) => !preset.includes(d)) };
    });
  };

  // R6+: fechas de SORTEO (el residente se anota, el admin sortea; sólo el ganador paga).
  // Una fecha no puede ser bloqueada Y sorteo a la vez: agregarla a una la quita de la otra.
  const addRaffleDate = (iso) => {
    if (!iso) return;
    setForm((f) => ({
      ...f,
      raffleDates: f.raffleDates.includes(iso) ? f.raffleDates : [...f.raffleDates, iso].sort(),
      blockedDates: f.blockedDates.filter((d) => d !== iso),
    }));
  };
  const removeRaffleDate = (iso) => {
    setForm((f) => ({ ...f, raffleDates: f.raffleDates.filter((d) => d !== iso) }));
  };
  const addRecurringRaffle = (mmdd) => {
    if (!mmdd) return;
    setForm((f) => ({
      ...f,
      recurringRaffleDates: f.recurringRaffleDates.includes(mmdd) ? f.recurringRaffleDates : [...f.recurringRaffleDates, mmdd].sort(),
      recurringBlockedDates: f.recurringBlockedDates.filter((d) => d !== mmdd),
    }));
  };
  const removeRecurringRaffle = (mmdd) => {
    setForm((f) => ({ ...f, recurringRaffleDates: f.recurringRaffleDates.filter((d) => d !== mmdd) }));
  };
  const addYearEndRaffle = () => {
    setForm((f) => {
      const preset = ["12-24", "12-25", "12-31", "01-01"];
      const merged = Array.from(new Set([...(f.recurringRaffleDates || []), ...preset])).sort();
      return { ...f, recurringRaffleDates: merged, recurringBlockedDates: (f.recurringBlockedDates || []).filter((d) => !preset.includes(d)) };
    });
  };

  const updateRule = (field, value) => {
    setForm((f) => ({ ...f, rules: { ...f.rules, [field]: Number(value) || 0 } }));
  };

  // ============================================================
  // Guardar / Eliminar
  // ============================================================
  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast("El nombre del amenity es obligatorio", "warning");
      return;
    }
    if (form.turns.length === 0) {
      showToast("Debe configurar al menos un turno", "warning");
      return;
    }
    for (const t of form.turns) {
      if (!t.name.trim()) {
        showToast("Todos los turnos deben tener nombre", "warning");
        return;
      }
    }

    setSaving(true);
    try {
      const docData = {
        buildingid: Number(buildingId),
        amenity: form.name.trim(), // mantiene campo legacy sincronizado
        name: form.name.trim(),
        description: form.description.trim(),
        imageUrl: form.imageUrl.trim(),
        category: form.category,
        currency: form.currency,
        requiresReservation: form.category === "con_costo" ? true : form.requiresReservation,
        turns: form.turns.map((t) => ({
          name: t.name.trim(),
          startTime: t.startTime,
          endTime: t.endTime,
          cost: form.category === "con_costo" ? Number(t.cost) || 0 : 0,
        })),
        // Mantiene campos legacy para compatibilidad
        costDay: form.category === "con_costo" && form.turns[0] ? Number(form.turns[0].cost) || 0 : 0,
        costNight: form.category === "con_costo" && form.turns[1] ? Number(form.turns[1].cost) || 0 : 0,
        availableDays: form.availableDays,
        blockedDates: Array.isArray(form.blockedDates) ? form.blockedDates : [],
        recurringBlockedDates: Array.isArray(form.recurringBlockedDates) ? form.recurringBlockedDates : [],
        raffleDates: Array.isArray(form.raffleDates) ? form.raffleDates : [],
        recurringRaffleDates: Array.isArray(form.recurringRaffleDates) ? form.recurringRaffleDates : [],
        exclusive: form.exclusive,
        maxCapacity: form.exclusive ? 1 : Number(form.maxCapacity) || 1,
        status: form.status,
        rules: {
          minAdvanceDays: Number(form.rules.minAdvanceDays) || 0,
          maxActiveReservationsPerUnit: Number(form.rules.maxActiveReservationsPerUnit) || 0,
          freeCancellationDays: Number(form.rules.freeCancellationDays) || 0,
        },
        requiresDeposit: form.requiresDeposit,
        depositAmount: form.requiresDeposit ? Number(form.depositAmount) || 0 : 0,
        requiresCleaning: form.requiresCleaning,
        cleaningEmployeeId: form.requiresCleaning ? form.cleaningEmployeeId : "",
        targetFundId: form.category === "con_costo" ? (form.targetFundId || "reserva") : "",
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "amenitiesbuilding", editingId), docData);
        showToast("Amenity actualizado", "success");
      } else {
        docData.createdAt = serverTimestamp();
        await addDoc(collection(db, "amenitiesbuilding"), docData);
        showToast("Amenity creado", "success");
      }

      closeForm();
      fetchAmenities();
    } catch (err) {
      console.error("Error guardando amenity:", err);
      showToast("Error al guardar amenity: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirmDialog(
      "¿Eliminar este amenity? Las reservas existentes no se eliminarán.",
      { title: "Eliminar amenity", danger: true, confirmLabel: "Eliminar" }
    );
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "amenitiesbuilding", id));
      setAmenities((prev) => prev.filter((a) => a.id !== id));
      showToast("Amenity eliminado", "success");
    } catch (err) {
      console.error("Error eliminando amenity:", err);
      showToast("Error al eliminar: " + err.message, "error");
    }
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div>
      {/* Cotizaciones BCU */}
      {!bcuLoading && bcuRate && (
        <div style={{ background: colors.primaryLighter, borderRadius: radius.md, padding: spacing[5], marginBottom: spacing[5] }}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing[3], marginBottom: spacing[4] }}>
            <div style={{ width: 40, height: 40, borderRadius: radius.sm, background: colors.white, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FaDollarSign size={18} color={colors.primary} />
            </div>
            <div>
              <div style={{ fontSize: typography.sizes.md, color: colors.black }}>Cotizaciones BCU</div>
              <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Actualizadas automáticamente todos los días a las 07:00 (UY)</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: spacing[5] }}>
            {(bcuRate.sell || bcuRate.buy) && (
              <div style={{ background: colors.white, borderRadius: radius.sm, padding: spacing[4] }}>
                <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: spacing[1] }}>Dólar (USD)</div>
                <div style={{ fontSize: typography.sizes["2xl"], color: colors.black }}>$ {(bcuRate.sell || bcuRate.buy)?.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            )}
            {bcuRate.ui && (
              <div style={{ background: colors.white, borderRadius: radius.sm, padding: spacing[4] }}>
                <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: spacing[1] }}>Unidad Indexada (UI)</div>
                <div style={{ fontSize: typography.sizes["2xl"], color: colors.black }}>$ {bcuRate.ui.toFixed(4)}</div>
              </div>
            )}
            {bcuRate.ur && (
              <div style={{ background: colors.white, borderRadius: radius.sm, padding: spacing[4] }}>
                <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: spacing[1] }}>Unidad Reajustable (UR)</div>
                <div style={{ fontSize: typography.sizes["2xl"], color: colors.black }}>$ {bcuRate.ur?.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            )}
          </div>
          <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: spacing[3] }}>
            Fecha: {bcuRate.date}{bcuRate.carriedFrom ? ` (último día hábil: ${bcuRate.carriedFrom})` : ""} — Fuente: Banco Central del Uruguay
          </div>
        </div>
      )}

      {/* Cabecera + botón agregar */}
      <div style={styles.sectionHeader}>
        <h3 style={styles.cardTitle}>Amenities del Edificio</h3>
        <button style={styles.addBtn} onClick={openCreate}>
          <FaPlus style={{ marginRight: 6 }} />
          Agregar Amenity
        </button>
      </div>

      {/* Cargando */}
      {loading && <LoadingState message="Cargando amenities…" />}

      {/* Estado vacío */}
      {!loading && amenities.length === 0 && !showForm && (
        <div style={styles.emptyCard}>
          <FaSwimmingPool style={{ fontSize: 40, color: colors.primary, opacity: 0.4, marginBottom: spacing[3] }} />
          <p style={{ color: colors.gray600, margin: 0 }}>
            No hay amenities configurados para este edificio.
          </p>
        </div>
      )}

      {/* Lista de amenities */}
      {!loading && amenities.length > 0 && (
      <div style={styles.amenityGrid}>
      {amenities.map((a) => (
        <div key={a.id} style={styles.amenityCard}>
          {/* Header: nombre + acciones (patrón unitHeader) */}
          <div style={styles.amenityCardHeader}>
            <h4 style={styles.amenityName}>{a.name || a.amenity}</h4>
            <div style={{ display: "flex", gap: 6 }}>
              <button style={styles.iconBtn} onClick={() => openEdit(a)} aria-label="Editar">
                <FaPen size={12} />
              </button>
              <button style={styles.iconBtnDanger} onClick={() => handleDelete(a.id)} aria-label="Eliminar">
                <FaTrash size={12} />
              </button>
            </div>
          </div>
          {a.description && <p style={styles.amenityDesc}>{a.description}</p>}
          {/* Badges */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: spacing[2] }}>
            <span style={{ ...styles.badge, backgroundColor: CATEGORY_COLORS[a.category] + "18", color: CATEGORY_COLORS[a.category] }}>
              {CATEGORY_LABELS[a.category] || a.category}
            </span>
            <span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[a.status] + "18", color: STATUS_COLORS[a.status] }}>
              {STATUS_LABELS[a.status] || a.status}
            </span>
          </div>

          {/* Resumen de turnos */}
          <div style={styles.turnsRow}>
            <FaClock style={{ color: colors.gray600, fontSize: 12 }} />
            {(a.turns || []).map((t, i) => (
              <span key={i} style={styles.turnChip}>
                {t.name} ({t.startTime}–{t.endTime})
                {a.category === "con_costo" && t.cost > 0 && (
                  <span> — {a.currency === "USD" ? "US$ " : a.currency === "UI" ? "UI " : a.currency === "UR" ? "UR " : "$ "}{t.cost}</span>
                )}
              </span>
            ))}
          </div>

          {/* Resumen de días */}
          <div style={styles.daysRow}>
            <FaCalendarDay style={{ color: colors.gray600, fontSize: 12 }} />
            {[1, 2, 3, 4, 5, 6, 0].map((d) => (
              <span key={d} style={{
                ...styles.dayChip,
                backgroundColor: (a.availableDays || []).includes(d) ? colors.primary : colors.gray200,
                color: (a.availableDays || []).includes(d) ? colors.white : colors.gray400,
              }}>
                {DAY_LABELS[d]}
              </span>
            ))}
          </div>

          {/* Fila de información */}
          <div style={styles.infoRow}>
            {a.exclusive ? (
              <span style={styles.infoChip}>Exclusivo (1 por turno)</span>
            ) : (
              <span style={styles.infoChip}>Compartido — Capacidad: {a.maxCapacity}</span>
            )}
            {a.category === "sin_costo" && (
              <span style={styles.infoChip}>
                {a.requiresReservation ? "Requiere reserva" : "Acceso libre"}
              </span>
            )}
            {a.rules?.minAdvanceDays > 0 && (
              <span style={styles.infoChip}>Min. {a.rules.minAdvanceDays} días anticipación</span>
            )}
            {a.rules?.freeCancellationDays > 0 && (
              <span style={styles.infoChip}>Cancelación gratis: {a.rules.freeCancellationDays} días</span>
            )}
          </div>

        </div>
      ))}
      </div>
      )}

      {/* ============ MODAL CREAR / EDITAR ============ */}
      {showForm && (
        <div style={styles.modalOverlayStyle}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {editingId ? "Editar Amenity" : "Nuevo Amenity"}
              </h3>
            </div>
            <div style={styles.modalBody}>
              {/* Nombre */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Nombre *</label>
                <input
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => updateForm("name", e.target.value)}
                  placeholder="Ej: Barbacoa, Piscina, Gimnasio"
                />
              </div>

              {/* Descripción */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Descripción</label>
                <textarea
                  style={{ ...styles.input, minHeight: 60, resize: "vertical" }}
                  value={form.description}
                  onChange={(e) => updateForm("description", e.target.value)}
                  placeholder="Descripción opcional del amenity"
                />
              </div>

              {/* Categoría + Moneda */}
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Categoría *</label>
                  <select
                    style={styles.input}
                    value={form.category}
                    onChange={(e) => updateForm("category", e.target.value)}
                  >
                    <option value="con_costo">Con costo</option>
                    <option value="sin_costo">Sin costo</option>
                  </select>
                </div>
                {form.category === "con_costo" && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Moneda</label>
                    <select
                      style={styles.input}
                      value={form.currency}
                      onChange={(e) => updateForm("currency", e.target.value)}
                    >
                      <option value="UYU">UYU (Pesos)</option>
                      <option value="USD">USD (Dólares)</option>
                      <option value="UI">UI (Unidad Indexada)</option>
                      <option value="UR">UR (Unidad Reajustable)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Requiere reserva (solo para sin_costo) */}
              {form.category === "sin_costo" && (
                <div style={styles.formGroup}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.requiresReservation}
                      onChange={(e) => updateForm("requiresReservation", e.target.checked)}
                      style={styles.checkbox}
                    />
                    Requiere reserva previa
                  </label>
                  <span style={styles.hint}>Si no, los residentes pueden acceder libremente sin reservar.</span>
                </div>
              )}

              {/* Exclusivo + Capacidad */}
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.exclusive}
                      onChange={(e) => updateForm("exclusive", e.target.checked)}
                      style={styles.checkbox}
                    />
                    Uso exclusivo (1 reserva por turno)
                  </label>
                </div>
                {!form.exclusive && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Capacidad máxima</label>
                    <input
                      type="number"
                      min="1"
                      style={styles.input}
                      value={form.maxCapacity}
                      onChange={(e) => updateForm("maxCapacity", e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Estado */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Estado</label>
                <select
                  style={styles.input}
                  value={form.status}
                  onChange={(e) => updateForm("status", e.target.value)}
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                  <option value="maintenance">En mantenimiento</option>
                </select>
              </div>

              {/* Días disponibles */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Días disponibles</label>
                <div style={styles.daysSelector}>
                  {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                    <button
                      key={d}
                      type="button"
                      style={{
                        ...styles.daySelectorBtn,
                        backgroundColor: form.availableDays.includes(d) ? colors.primary : colors.gray100,
                        color: form.availableDays.includes(d) ? colors.white : colors.gray600,
                      }}
                      onClick={() => toggleDay(d)}
                    >
                      {DAY_LABELS[d]}
                    </button>
                  ))}
                </div>
              </div>

              {/* R6: Fechas bloqueadas */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Fechas bloqueadas</label>
                <p style={{ fontSize: 12, color: colors.gray500, margin: "0 0 8px" }}>
                  Días en los que el amenity NO se puede reservar (ej: 24, 25 y 31 de diciembre, 1 de enero).
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                  <input
                    type="date"
                    style={{ ...styles.input, width: 170 }}
                    value={blockDateInput}
                    onChange={(e) => setBlockDateInput(e.target.value)}
                  />
                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={() => { addBlockedDate(blockDateInput); setBlockDateInput(""); }}
                  >
                    + Fecha puntual
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={() => { if (blockDateInput) { addRecurringBlocked(blockDateInput.slice(5)); setBlockDateInput(""); } }}
                    title="Bloquea ese día y mes todos los años"
                  >
                    + Todos los años
                  </button>
                  <button type="button" style={styles.secondaryBtn} onClick={addYearEndHolidays}>
                    Preset feriados fin de año
                  </button>
                </div>
                {form.blockedDates.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: colors.gray500, marginBottom: 4 }}>Puntuales (una vez)</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {form.blockedDates.map((d) => (
                        <span key={d} style={styles.dateChip}>
                          {d}
                          <button type="button" style={styles.chipRemove} onClick={() => removeBlockedDate(d)}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {form.recurringBlockedDates.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, color: colors.gray500, marginBottom: 4 }}>Recurrentes (todos los años, MM-DD)</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {form.recurringBlockedDates.map((d) => (
                        <span key={d} style={{ ...styles.dateChip, background: colors.infoLight, color: colors.infoDark }}>
                          {d}
                          <button type="button" style={styles.chipRemove} onClick={() => removeRecurringBlocked(d)}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* R6+: Fechas de sorteo */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Fechas de sorteo</label>
                <p style={{ fontSize: 12, color: colors.gray500, margin: "0 0 8px" }}>
                  Días de alta demanda: el residente no reserva directo, se <strong>anota al sorteo</strong> (aparecen en amarillo). Después la admin sortea quién la usa y sólo a esa persona se le cobra en los gastos comunes.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                  <input
                    type="date"
                    style={{ ...styles.input, width: 170 }}
                    value={raffleDateInput}
                    onChange={(e) => setRaffleDateInput(e.target.value)}
                  />
                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={() => { addRaffleDate(raffleDateInput); setRaffleDateInput(""); }}
                  >
                    + Fecha puntual
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={() => { if (raffleDateInput) { addRecurringRaffle(raffleDateInput.slice(5)); setRaffleDateInput(""); } }}
                    title="Sortea ese día y mes todos los años"
                  >
                    + Todos los años
                  </button>
                  <button type="button" style={styles.secondaryBtn} onClick={addYearEndRaffle}>
                    Preset feriados fin de año
                  </button>
                </div>
                {form.raffleDates.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: colors.gray500, marginBottom: 4 }}>Puntuales (una vez)</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {form.raffleDates.map((d) => (
                        <span key={d} style={{ ...styles.dateChip, background: colors.warningLight, color: colors.warningDark }}>
                          {d}
                          <button type="button" style={styles.chipRemove} onClick={() => removeRaffleDate(d)}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {form.recurringRaffleDates.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, color: colors.gray500, marginBottom: 4 }}>Recurrentes (todos los años, MM-DD)</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {form.recurringRaffleDates.map((d) => (
                        <span key={d} style={{ ...styles.dateChip, background: colors.warningLight, color: colors.warningDark }}>
                          {d}
                          <button type="button" style={styles.chipRemove} onClick={() => removeRecurringRaffle(d)}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Turnos */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Turnos</label>
                {form.turns.map((t, idx) => (
                  <div key={idx} style={styles.turnRow}>
                    <input
                      style={{ ...styles.input, flex: 1 }}
                      value={t.name}
                      onChange={(e) => updateTurn(idx, "name", e.target.value)}
                      placeholder="Nombre del turno"
                    />
                    <input
                      type="time"
                      style={{ ...styles.input, width: 110 }}
                      value={t.startTime}
                      onChange={(e) => updateTurn(idx, "startTime", e.target.value)}
                    />
                    <span style={{ color: colors.gray600 }}>a</span>
                    <input
                      type="time"
                      style={{ ...styles.input, width: 110 }}
                      value={t.endTime}
                      onChange={(e) => updateTurn(idx, "endTime", e.target.value)}
                    />
                    {form.category === "con_costo" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <FaDollarSign style={{ color: colors.gray600, fontSize: 12 }} />
                        <input
                          type="number"
                          min="0"
                          style={{ ...styles.input, width: 90 }}
                          value={t.cost}
                          onChange={(e) => updateTurn(idx, "cost", e.target.value)}
                          placeholder="Costo"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      style={styles.turnRemoveBtn}
                      onClick={() => removeTurn(idx)}
                      title="Eliminar turno"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
                <button type="button" style={styles.addTurnBtn} onClick={addTurn}>
                  <FaPlus style={{ marginRight: 4 }} /> Agregar turno
                </button>
              </div>

              {/* Reglas */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Reglas de reserva</label>
                <div style={styles.rulesGrid}>
                  <div style={styles.ruleItem}>
                    <label style={styles.ruleLabel}>Días mínimos de anticipación</label>
                    <input
                      type="number"
                      min="0"
                      style={styles.input}
                      value={form.rules.minAdvanceDays}
                      onChange={(e) => updateRule("minAdvanceDays", e.target.value)}
                    />
                  </div>
                  <div style={styles.ruleItem}>
                    <label style={styles.ruleLabel}>Máx. reservas activas por unidad</label>
                    <input
                      type="number"
                      min="0"
                      style={styles.input}
                      value={form.rules.maxActiveReservationsPerUnit}
                      onChange={(e) => updateRule("maxActiveReservationsPerUnit", e.target.value)}
                    />
                    <span style={styles.hint}>0 = sin límite</span>
                  </div>
                  <div style={styles.ruleItem}>
                    <label style={styles.ruleLabel}>Días de cancelación gratuita</label>
                    <input
                      type="number"
                      min="0"
                      style={styles.input}
                      value={form.rules.freeCancellationDays}
                      onChange={(e) => updateRule("freeCancellationDays", e.target.value)}
                    />
                    <span style={styles.hint}>Si el residente cancela con esta anticipación o más, se revierte el cargo. 0 = siempre gratis.</span>
                  </div>
                </div>
              </div>

              {/* Depósito (futuro) */}
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.requiresDeposit}
                    onChange={(e) => updateForm("requiresDeposit", e.target.checked)}
                    style={styles.checkbox}
                  />
                  Requiere seña/depósito
                </label>
                {form.requiresDeposit && (
                  <input
                    type="number"
                    min="0"
                    style={{ ...styles.input, marginTop: spacing[2] }}
                    value={form.depositAmount}
                    onChange={(e) => updateForm("depositAmount", e.target.value)}
                    placeholder="Monto del depósito"
                  />
                )}
              </div>

              {/* Fondo destino de ingresos (solo con costo) */}
              {form.category === "con_costo" && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Fondo destino de ingresos</label>
                  <select
                    style={styles.input}
                    className="habitia-input"
                    value={form.targetFundId}
                    onChange={(e) => updateForm("targetFundId", e.target.value)}
                  >
                    {activeFunds.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                  <span style={styles.hint}>El ingreso por reservas se acreditará en este fondo al cierre de mes.</span>
                </div>
              )}

              {/* Limpieza post-uso */}
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.requiresCleaning}
                    onChange={(e) => updateForm("requiresCleaning", e.target.checked)}
                    style={styles.checkbox}
                  />
                  <FaBroom style={{ marginRight: 4, color: colors.gray600, fontSize: 13 }} />
                  Requiere limpieza post-uso
                </label>
                <span style={styles.hint}>Al confirmar una reserva se creará automáticamente una tarea de limpieza para los empleados.</span>
                {form.requiresCleaning && (
                  <div style={{ marginTop: spacing[2] }}>
                    <label style={styles.ruleLabel}>Empleado asignado</label>
                    <select
                      style={styles.input}
                      className="habitia-input"
                      value={form.cleaningEmployeeId}
                      onChange={(e) => updateForm("cleaningEmployeeId", e.target.value)}
                    >
                      <option value="">Todos los empleados del edificio</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.employeeid}>
                          {`${emp.nombre || ""} ${emp.apellido || ""}`.trim() || emp.email || emp.employeeid}
                        </option>
                      ))}
                    </select>
                    {employees.length === 0 && (
                      <span style={{ ...styles.hint, color: colors.warning }}>
                        No hay empleados asignados a este edificio. La tarea no se creará hasta que haya empleados.
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Pie del modal */}
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={closeForm}>
                Cancelar
              </button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <span style={styles.spinner} />
                    Guardando...
                  </>
                ) : (
                  <>
                    <FaCheck style={{ marginRight: 4 }} />
                    {editingId ? "Guardar" : "Crear"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Estilos (design tokens)
// ============================================================
const styles = {
  // Cabecera de sección
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing[4],
  },
  // Título de card
  cardTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: 0,
  },
  // Texto de carga
  // Card estado vacío — sin borde, solo sombra
  emptyCard: {
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing[10],
  },
  // Grid de amenities — mismo layout que unidades
  amenityGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: spacing[4],
  },
  // Card de amenity — mismo estilo que unitCard
  amenityCard: {
    borderRadius: radius.md,
    padding: spacing[4],
    backgroundColor: colors.gray50,
    boxShadow: shadows.xs,
  },
  // Cabecera del card de amenity
  amenityCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  // Nombre del amenity
  amenityName: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: 0,
  },
  // Descripción del amenity
  amenityDesc: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: `0 0 ${spacing[2]}px 0`,
  },
  // Badge genérico
  badge: {
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    padding: "3px 10px",
    borderRadius: radius.full,
    whiteSpace: "nowrap",
  },
  // Fila de turnos
  turnsRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    flexWrap: "wrap",
    marginBottom: spacing[2],
  },
  // Chip de turno
  turnChip: {
    fontSize: typography.sizes.sm,
    padding: "4px 10px",
    borderRadius: radius.sm,
    backgroundColor: colors.gray100,
    color: colors.gray700,
  },
  // Fila de días
  daysRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[1],
    marginBottom: spacing[2],
  },
  // Chip de día
  dayChip: {
    fontSize: typography.sizes.xs,
    fontWeight: 400,
    padding: "2px 8px",
    borderRadius: 4,
    textAlign: "center",
  },
  // Fila de información
  infoRow: {
    display: "flex",
    gap: spacing[2],
    flexWrap: "wrap",
    marginBottom: spacing[3],
  },
  // Chip de información
  infoChip: {
    fontSize: typography.sizes.xs,
    padding: "3px 8px",
    borderRadius: 4,
    backgroundColor: colors.warningLight,
    color: colors.warningDark,
  },
  // Botón agregar — basado en btnPrimary
  addBtn: {
    ...btnPrimary,
    fontSize: typography.sizes.base,
  },
  // Botón editar — icono, patrón tarjeta de unidades
  iconBtn: {
    ...btnGhost,
    background: colors.primarySubtle,
    color: colors.primary,
    borderRadius: radius.sm,
  },
  // Botón eliminar — icono, patrón tarjeta de unidades
  iconBtnDanger: {
    ...btnGhost,
    background: colors.errorLight,
    color: colors.errorDark,
    borderRadius: radius.sm,
  },
  // Overlay del modal — basado en token modalOverlay
  modalOverlayStyle: {
    ...modalOverlay,
  },
  // Contenedor del modal
  modal: {
    backgroundColor: colors.white,
    borderRadius: radius.lg,
    width: 640,
    maxWidth: "92vw",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: shadows.modal,
    fontFamily: typography.fontFamily,
  },
  // Cabecera del modal
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${spacing[5]}px ${spacing[6]}px ${spacing[4]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  // Título del modal
  modalTitle: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    margin: 0,
    color: colors.black,
  },
  // Botón cancelar — basado en btnSecondary
  cancelBtn: {
    ...btnSecondary,
    color: colors.gray600,
  },
  // Cuerpo del modal
  modalBody: {
    padding: `${spacing[4]}px ${spacing[6]}px`,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: spacing[4],
  },
  // Pie del modal
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: spacing[3],
    padding: `${spacing[4]}px ${spacing[6]}px`,
    borderTop: `1px solid ${colors.gray200}`,
  },
  // Grupo de formulario
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  // Fila de formulario
  formRow: {
    display: "flex",
    gap: spacing[4],
    flexWrap: "wrap",
  },
  // Etiqueta de campo
  label: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray900,
  },
  // Input base — usa token inputBase
  input: {
    ...inputBase,
    padding: "9px 12px",
    backgroundColor: colors.gray50,
    color: colors.black,
  },
  // Texto de ayuda
  hint: {
    fontSize: typography.sizes.xs,
    color: colors.gray400,
  },
  // Etiqueta de checkbox
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.md,
    color: colors.gray900,
    cursor: "pointer",
  },
  // Checkbox
  checkbox: {
    width: 16,
    height: 16,
    cursor: "pointer",
  },
  // Selector de días
  daysSelector: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  // Botón selector de día
  daySelectorBtn: {
    padding: "6px 12px",
    borderRadius: radius.sm,
    border: "none",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
    transition: `all ${transitions.fast}`,
  },
  // R6: botón secundario y chips de fecha bloqueada
  secondaryBtn: {
    padding: "8px 12px",
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray300}`,
    background: colors.white,
    color: colors.gray900,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
    transition: `all ${transitions.fast}`,
  },
  dateChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 8px",
    borderRadius: radius.md,
    background: colors.gray100,
    color: colors.gray900,
    fontSize: typography.sizes.sm,
  },
  chipRemove: {
    border: "none",
    background: "transparent",
    color: "inherit",
    cursor: "pointer",
    fontSize: 15,
    lineHeight: 1,
    padding: 0,
  },
  // Fila de turno
  turnRow: {
    display: "flex",
    gap: spacing[2],
    alignItems: "center",
    marginBottom: spacing[2],
    flexWrap: "wrap",
  },
  // Botón eliminar turno
  turnRemoveBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: radius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: colors.white,
    color: colors.error,
    cursor: "pointer",
    fontSize: typography.sizes.sm,
  },
  // Botón agregar turno
  addTurnBtn: {
    display: "flex",
    alignItems: "center",
    padding: "6px 14px",
    borderRadius: radius.sm,
    border: `1px dashed ${colors.primary}`,
    backgroundColor: "transparent",
    color: colors.primary,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
  },
  // Grilla de reglas
  rulesGrid: {
    display: "flex",
    gap: spacing[4],
    flexWrap: "wrap",
  },
  // Item de regla
  ruleItem: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
    minWidth: 160,
    flex: 1,
  },
  // Etiqueta de regla
  ruleLabel: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    fontWeight: 400,
  },
  // Botón guardar — basado en btnPrimary
  saveBtn: {
    ...btnPrimary,
  },
  // Spinner de carga
  spinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: `2px solid ${colors.white}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginRight: 6,
  },
};

export default AmenityConfigTab;
